import { useCart } from "../../context/CartContext";
import {
  X,
  Trash2,
  Minus,
  Plus,
  ShoppingBag,
  MessageCircle,
  Mail,
} from "lucide-react";
import {
  openWhatsAppOrder,
  openEmailOrder,
  ORDER_WHATSAPP_DISPLAY,
  ORDER_EMAIL,
} from "../../lib/order";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const absUrl = (u) => {
  if (!u) return "";
  if (u.startsWith("http")) return u;
  if (u.startsWith("/api/files/") || u.startsWith("/uploads/"))
    return `${BACKEND}${u}`;
  return u;
};

const money = (n) =>
  Number(n).toLocaleString("ro-RO", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

export const CartDrawer = () => {
  const { items, open, setOpen, setQty, remove, clear, totalQty, totalPrice } =
    useCart();

  if (!open) return null;

  const empty = items.length === 0;

  return (
    <div
      className="fixed inset-0 z-[60] flex justify-end bg-black/70 backdrop-blur-sm"
      onClick={() => setOpen(false)}
      data-testid="cart-drawer"
    >
      <aside
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md h-full bg-[#0A0908] border-l border-[#1F1A18] flex flex-col"
      >
        <header className="flex items-center justify-between px-6 py-5 border-b border-[#1F1A18]">
          <div>
            <p className="text-[10px] tracking-[0.32em] uppercase text-[#C8A97E]">
              — Coș
            </p>
            <h3 className="font-[Playfair_Display] text-xl mt-1">
              {totalQty} {totalQty === 1 ? "produs" : "produse"}
            </h3>
          </div>
          <button
            data-testid="cart-close"
            onClick={() => setOpen(false)}
            className="p-2 border border-[#2A2522] hover:border-[#C8A97E] transition-colors"
          >
            <X size={16} />
          </button>
        </header>

        <div className="flex-1 overflow-y-auto">
          {empty ? (
            <div
              className="h-full flex flex-col items-center justify-center px-8 text-center"
              data-testid="cart-empty"
            >
              <ShoppingBag
                size={48}
                strokeWidth={1}
                className="text-[#3D3530] mb-6"
              />
              <p className="font-[Playfair_Display] text-2xl">
                Coșul tău e <em className="italic text-[#C8A97E]">gol.</em>
              </p>
              <p className="text-[#9D8B7B] text-sm font-light mt-3">
                Adaugă produse din catalog pentru a trimite o comandă.
              </p>
            </div>
          ) : (
            <ul className="divide-y divide-[#1F1A18]">
              {items.map((it) => (
                <li
                  key={it.id}
                  data-testid={`cart-item-${it.id}`}
                  className="p-5 flex gap-4"
                >
                  <div className="w-20 h-20 bg-white flex-shrink-0 border border-[#2A2522] overflow-hidden">
                    {it.image ? (
                      <img
                        src={absUrl(it.image)}
                        alt={it.name}
                        className="w-full h-full object-cover"
                      />
                    ) : null}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-[10px] uppercase tracking-[0.2em] text-[#C8A97E]">
                      {it.category}
                    </p>
                    <p className="font-[Playfair_Display] text-[#FDF8F5] leading-snug mt-1">
                      {it.name}
                    </p>
                    <p className="text-xs text-[#9D8B7B] font-light mt-1">
                      {[it.size, it.pack].filter(Boolean).join(" · ") || "—"}
                    </p>

                    <div className="mt-3 flex items-center justify-between">
                      <div className="flex items-center border border-[#2A2522]">
                        <button
                          data-testid={`cart-dec-${it.id}`}
                          onClick={() => setQty(it.id, it.qty - 1)}
                          className="w-8 h-8 flex items-center justify-center hover:bg-[#0F0D0B]"
                        >
                          <Minus size={12} />
                        </button>
                        <span className="w-9 text-center text-sm">
                          {it.qty}
                        </span>
                        <button
                          data-testid={`cart-inc-${it.id}`}
                          onClick={() => setQty(it.id, it.qty + 1)}
                          className="w-8 h-8 flex items-center justify-center hover:bg-[#0F0D0B]"
                        >
                          <Plus size={12} />
                        </button>
                      </div>
                      <div className="text-right">
                        {it.price != null && (
                          <p className="text-sm text-[#C8A97E]">
                            {money(it.price * it.qty)} lei
                          </p>
                        )}
                        <button
                          data-testid={`cart-remove-${it.id}`}
                          onClick={() => remove(it.id)}
                          className="text-[10px] uppercase tracking-[0.2em] text-[#9D8B7B] hover:text-red-400 flex items-center gap-1 mt-1 ml-auto"
                        >
                          <Trash2 size={11} /> Șterge
                        </button>
                      </div>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        {!empty && (
          <footer className="border-t border-[#1F1A18] p-6 space-y-4 bg-[#0A0908]">
            {totalPrice > 0 && (
              <div className="flex items-center justify-between">
                <span className="text-xs uppercase tracking-[0.2em] text-[#9D8B7B]">
                  Total estimativ
                </span>
                <span
                  className="font-[Playfair_Display] text-2xl text-[#C8A97E]"
                  data-testid="cart-total"
                >
                  {money(totalPrice)} <span className="text-sm">lei</span>
                </span>
              </div>
            )}

            <button
              data-testid="checkout-whatsapp"
              onClick={() => openWhatsAppOrder(items, totalPrice)}
              className="w-full flex items-center justify-center gap-2 bg-[#25D366] text-[#0A2E17] py-4 text-sm font-medium tracking-wide hover:bg-[#20bd5c] transition-colors"
            >
              <MessageCircle size={16} strokeWidth={2} />
              Finalizează pe WhatsApp
            </button>
            <button
              data-testid="checkout-email"
              onClick={() => openEmailOrder(items, totalPrice)}
              className="w-full flex items-center justify-center gap-2 bg-[#C8A97E] text-[#080706] py-4 text-sm font-medium tracking-wide hover:bg-[#B3956D] transition-colors"
            >
              <Mail size={16} strokeWidth={1.6} />
              Finalizează pe email
            </button>

            <div className="text-center text-[10px] uppercase tracking-[0.2em] text-[#6B5E55] pt-1 space-y-0.5">
              <p>{ORDER_WHATSAPP_DISPLAY}</p>
              <p className="normal-case tracking-normal">{ORDER_EMAIL}</p>
            </div>

            <button
              data-testid="cart-clear"
              onClick={clear}
              className="w-full text-[10px] uppercase tracking-[0.22em] text-[#6B5E55] hover:text-red-400 transition-colors"
            >
              Golește coșul
            </button>
          </footer>
        )}
      </aside>
    </div>
  );
};
